import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Activity, Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Das Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    if (password !== confirm) {
      toast.error('Die Passwörter stimmen nicht überein');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.resetPassword(token, password);
      toast.success(res.data.message);
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zurücksetzen fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-8 neon-glow">
          <Activity className="w-6 h-6 text-primary" />
        </div>
        <div className="mb-8">
          <h2 className="font-heading font-bold text-3xl mb-2">Neues Passwort</h2>
          <p className="text-muted-foreground text-sm">
            {token
              ? 'Wähle ein neues Passwort für dein Konto. Alle offenen Sitzungen werden abgemeldet.'
              : 'Dieser Link ist unvollständig. Bitte fordere einen neuen Link an.'}
          </p>
        </div>

        {token && (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
            <div className="space-y-2">
              <Label htmlFor="password">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  placeholder="Min. 8 Zeichen"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-background pr-10"
                  required
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="reset-toggle-password-btn"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Passwort wiederholen</Label>
              <Input
                id="confirm"
                type={showPw ? 'text' : 'password'}
                placeholder="Min. 8 Zeichen"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-12 bg-background"
                required
                data-testid="reset-confirm-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 font-bold tracking-wider uppercase bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="reset-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Passwort speichern
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-muted-foreground mt-8">
          <Link to="/passwort-vergessen" className="text-primary hover:underline font-medium" data-testid="request-new-link">
            Neuen Link anfordern
          </Link>
        </p>
      </div>
    </div>
  );
}
